import React, { useEffect, lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { useDispatch } from "react-redux";
import { fetchJoinData } from "src/redux/slices/menuSlice";
import { LastVisitedProvider } from "./context";
import HomePage from "./pages/home_page";
import "./global.css";
import { CartProvider } from "./components/QrScanMenu/Menu/cartContext/cartContext";

// Lazy components
const RootLayout = lazy(() => import("./layout/root_layout"));
const NotFoundPage = lazy(() => import("./pages/not_found_page"));
const MenuPage = lazy(() => import("./pages/menu_page"));
const AboutPage = lazy(() => import("./pages/about_page"));
const ContactPage = lazy(() => import("./pages/contact_page"));
const OfferSection = lazy(() =>
  import("./components/offerSection/offerSection")
);
const DesPage = lazy(() =>
  import("./components/foodDescriptionPage/loadPage")
);
const ProfilePage = lazy(() =>
  import("./layout/nav_bar/profile/profileLoad")
);
const TrackPage = lazy(() =>
  import("./section/section_track/trackPage/trackPageLoad")
);
const FAQSection = lazy(() => import("./section/faqSection/faqSection"));
const MakeMenu = lazy(() =>
  import("./views/menu/components/make_menu/make_menu")
);
const SubscriptionPage = lazy(() =>
  import("./pages/Subscription/subscription_page")
);
const SubscriptionPackages = lazy(() =>
  import("./pages/Subscription/subscriptionPackagesPage")
);
const AvailablePlans = lazy(() =>
  import("./pages/Subscription/available_Plans/AvailablePlanCard")
);
const CheckoutSection = lazy(() =>
  import("./components/order_confirmation/checkout_section")
);
const Setting = lazy(() =>
  import("./layout/nav_bar/profile/setting/settingLoad")
);
const LoginForm = lazy(() =>
  import("./layout/nav_bar/form/loginInfo/LoginForm")
);
const SignUpForm = lazy(() =>
  import("./layout/nav_bar/form/signupForm/sign_upform")
);
const VegItemsListing = lazy(() =>
  import("src/views/vegItems/veg_items")
);
const NonVegItems = lazy(() =>
  import("src/views/Non-veg/nonVeg")
);
const BookTable = lazy(() =>
  import("./components/order_list/book_table/book_table")
);
const ProfileLoad = lazy(() => import("./components/profileLoad"));
const EsewaSuccess = lazy(() =>
  import("./components/order_confirmation/payment/esewa_success")
);
const EsewaFailed = lazy(() =>
  import("./components/order_confirmation/payment/esewa_failure")
);
const QrMenu = lazy(() =>
  import("src/components/QrScanMenu/Menu/itemListing")
);

// Reusable Suspense wrapper
const Lazy = ({ children }) => (
  <Suspense fallback={<div className="page-loader">Loading...</div>}>
    {children}
  </Suspense>
);

function App() {
  const dispatch = useDispatch();

  // Defer heavy Redux call to avoid blocking UI
 useEffect(() => {
  const timeoutId = setTimeout(() => {
    dispatch(fetchJoinData());
  }, 0);

  return () => clearTimeout(timeoutId);
}, [dispatch]);


  return (
    <LastVisitedProvider>
      <CartProvider>
        <Router>
          <Routes>
            <Route
              path="/"
              element={
                <Lazy>
                  <RootLayout />
                </Lazy>
              }
            >
              <Route index element={<HomePage />} />
              <Route path="make-your-menu" element={<Lazy><MakeMenu /></Lazy>} />
              <Route path="description" element={<Lazy><DesPage /></Lazy>} />
              <Route path="offer" element={<Lazy><OfferSection /></Lazy>} />
              <Route path="profile" element={<Lazy><ProfilePage /></Lazy>} />
              <Route path="track" element={<Lazy><TrackPage /></Lazy>} />
              <Route path="menu" element={<Lazy><MenuPage /></Lazy>} />
              <Route path="about" element={<Lazy><AboutPage /></Lazy>} />
              <Route path="contact" element={<Lazy><ContactPage /></Lazy>} />
              <Route path="faq" element={<Lazy><FAQSection /></Lazy>} />
              <Route path="setting" element={<Lazy><Setting /></Lazy>} />
              <Route path="login" element={<Lazy><LoginForm /></Lazy>} />
              <Route path="signup" element={<Lazy><SignUpForm /></Lazy>} />
              <Route path="subscription" element={<Lazy><SubscriptionPage /></Lazy>} />
              <Route
                path="subscriptionPackage"
                element={<Lazy><SubscriptionPackages /></Lazy>}
              />
              <Route
                path="availableplans"
                element={<Lazy><AvailablePlans /></Lazy>}
              />
              <Route
                path="availableplans/:frequency"
                element={<Lazy><AvailablePlans /></Lazy>}
              />
              <Route path="checkout" element={<Lazy><CheckoutSection /></Lazy>} />
              <Route path="veg" element={<Lazy><VegItemsListing /></Lazy>} />
              <Route path="nonveg" element={<Lazy><NonVegItems /></Lazy>} />
              <Route path="book-your-table" element={<Lazy><BookTable /></Lazy>} />
              <Route path="profile/:customerId" element={<Lazy><ProfileLoad /></Lazy>} />
              <Route path="qrMenu" element={<Lazy><QrMenu /></Lazy>} />
              <Route path="*" element={<Lazy><NotFoundPage /></Lazy>} />
            </Route>

            <Route path="esewa-success" element={<Lazy><EsewaSuccess /></Lazy>} />
            <Route path="esewa-failed" element={<Lazy><EsewaFailed /></Lazy>} />
          </Routes>
        </Router>
      </CartProvider>
    </LastVisitedProvider>
  );
  
}

export default App;
